import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { ArrowLeft, Plus, Trash2, Loader2, CheckCircle2, Save, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { SearchableContactSelect } from '../components/ui/searchable-contact-select';
import { SearchableAccountSelect } from '../components/ui/searchable-account-select';
import { SearchableDescriptionSelect } from '../components/ui/searchable-description-select';
import { CurrencyInput } from '../components/ui/currency-input';
import { AddressPicker } from '../components/AddressPicker';
import InvoicePayments from '../components/invoice/InvoicePayments';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(a || 0);

export default function InvoiceForm() {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const { currentWorkspace } = useWorkspace();
  const isEdit = Boolean(invoiceId);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [descriptions, setDescriptions] = useState([]);
  const [defaultTaxRate, setDefaultTaxRate] = useState(0);
  // Cache of currently-selected customer's full addresses
  const [customerAddresses, setCustomerAddresses] = useState([]);

  const [form, setForm] = useState({
    customer_id: '',
    invoice_no: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    memo: '',
    reference: '',
    billing_address_id: null,
    shipping_address_id: null,
    line_items: [{ description: '', quantity: 1, unit_price: 0, account_id: '', tax_rate: 0 }],
  });

  // Load workspace lookups
  useEffect(() => {
    if (!currentWorkspace) return;
    (async () => {
      try {
        const [c, a, d, s] = await Promise.all([
          apiFetch(`${API_URL}/api/contacts?workspace_id=${currentWorkspace.workspace_id}&contact_type=customer`),
          apiFetch(`${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/accounts`),
          apiFetch(`${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/predefined-descriptions`),
          apiFetch(`${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/settings`),
        ]);
        if (c.ok) setCustomers(await c.json());
        if (a.ok) {
          const data = await a.json();
          setAccounts(data.filter(x => x.is_active !== false));
        }
        if (d.ok) setDescriptions(await d.json());
        if (s.ok) {
          const settings = await s.json();
          if (!isEdit && settings.default_tax_rate) setDefaultTaxRate(Number(settings.default_tax_rate) || 0);
        }
      } catch (e) { console.error(e); }
    })();
  }, [currentWorkspace, isEdit]);

  // Load invoice when editing
  useEffect(() => {
    if (!isEdit || !currentWorkspace) return;
    (async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/invoices/${invoiceId}?workspace_id=${currentWorkspace.workspace_id}`);
        if (!res.ok) {
          toast.error('Invoice not found');
          navigate('/invoices');
          return;
        }
        const inv = await res.json();
        setForm({
          customer_id: inv.customer_id || '',
          invoice_no: inv.invoice_no?.toString() || '',
          invoice_date: inv.invoice_date || new Date().toISOString().split('T')[0],
          due_date: inv.due_date || '',
          memo: inv.memo || '',
          reference: inv.reference || '',
          billing_address_id: inv.billing_address_id || null,
          shipping_address_id: inv.shipping_address_id || null,
          line_items: (inv.line_items || []).map(l => ({
            description: l.description || '',
            quantity: l.quantity || 1,
            unit_price: l.unit_price || 0,
            account_id: l.account_id || '',
            tax_rate: l.tax_rate || 0,
          })),
        });
        setCustomerAddresses(inv.customer_addresses || []);
      } catch (e) {
        toast.error('Failed to load invoice');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, invoiceId, currentWorkspace, navigate]);

  // Default due date for new invoices
  useEffect(() => {
    if (isEdit) return;
    if (!form.due_date) {
      const dd = new Date();
      dd.setDate(dd.getDate() + 30);
      setForm(f => ({ ...f, due_date: dd.toISOString().split('T')[0] }));
    }
  }, [isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  // When a customer is selected (or changed), load that customer's addresses
  useEffect(() => {
    if (!form.customer_id) {
      setCustomerAddresses([]);
      return;
    }
    (async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/contacts/${form.customer_id}`);
        if (res.ok) {
          const c = await res.json();
          setCustomerAddresses(c.addresses || []);
        }
      } catch {}
    })();
  }, [form.customer_id]);

  const addLine = () => setForm(f => ({
    ...f,
    line_items: [...f.line_items, { description: '', quantity: 1, unit_price: 0, account_id: '', tax_rate: defaultTaxRate }]
  }));
  const removeLine = (idx) => setForm(f => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));
  const updateLine = (idx, field, value) => setForm(f => ({
    ...f,
    line_items: f.line_items.map((line, i) => i === idx ? { ...line, [field]: value } : line)
  }));

  const subtotal = form.line_items.reduce((s, l) => s + (l.quantity * l.unit_price), 0);
  const tax = form.line_items.reduce((s, l) => s + (l.quantity * l.unit_price * l.tax_rate / 100), 0);
  const total = subtotal + tax;

  const handleSave = async () => {
    if (!form.customer_id) { toast.error('Please select a customer'); return; }
    if (form.line_items.every(l => !l.description || !l.account_id)) {
      toast.error('Please add at least one complete line item');
      return;
    }
    setSaving(true);
    try {
      const url = isEdit
        ? `${API_URL}/api/invoices/${invoiceId}?workspace_id=${currentWorkspace.workspace_id}`
        : `${API_URL}/api/invoices?workspace_id=${currentWorkspace.workspace_id}`;
      const res = await apiFetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          line_items: form.line_items.filter(l => l.description && l.account_id),
        }),
      });
      if (res.ok) {
        const saved = await res.json();
        toast.success(isEdit ? 'Invoice updated' : 'Invoice created');
        navigate(`/invoices`);
        return saved;
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Failed to ${isEdit ? 'update' : 'create'} invoice`);
      }
    } catch {
      toast.error(`Failed to ${isEdit ? 'update' : 'create'} invoice`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-center text-muted-foreground">Loading invoice…</div>;

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="invoice-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/invoices')} data-testid="invoice-form-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="w-6 h-6" /> {isEdit ? 'Edit Invoice' : 'New Invoice'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit ? 'Update invoice details, line items, and addresses.' : 'Create a draft invoice. You can edit and post it after saving.'}
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Invoice #</Label>
              <Input
                value={form.invoice_no || ''}
                onChange={(e) => setForm({ ...form, invoice_no: e.target.value })}
                placeholder="Auto-generated"
                data-testid="invoice-number-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Customer *</Label>
              <SearchableContactSelect
                type="customer" contacts={customers} value={form.customer_id}
                onValueChange={(v) => setForm({ ...form, customer_id: v, billing_address_id: null, shipping_address_id: null })}
                workspaceId={currentWorkspace?.workspace_id}
                onContactCreated={(c) => setCustomers(prev => [...prev, c])}
                placeholder="Search or add customer..." testId="customer-select"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reference #</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="PO-001" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Invoice Date</Label>
              <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Due Date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Address pickers — surface as soon as a customer is selected */}
      {form.customer_id && customerAddresses.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4">
              <AddressPicker
                addresses={customerAddresses}
                value={form.billing_address_id}
                onChange={(id) => setForm({ ...form, billing_address_id: id })}
                label="Bill To"
                preferType="billing"
                testId="invoice-bill-to"
              />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <AddressPicker
                addresses={customerAddresses}
                value={form.shipping_address_id}
                onChange={(id) => setForm({ ...form, shipping_address_id: id })}
                label="Ship To"
                preferType="shipping"
                testId="invoice-ship-to"
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Line items */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Line Items</Label>
          <div className="border rounded-xl overflow-hidden bg-muted">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="text-xs font-semibold uppercase tracking-wider min-w-[180px]">Description</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider min-w-[160px]">Account</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider w-[65px] text-center">Qty</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider w-[130px] text-right">Price</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider w-[90px] text-center">Tax %</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider w-[120px] text-right">Amount</TableHead>
                  <TableHead className="w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {form.line_items.map((line, idx) => (
                  <TableRow key={idx} className="bg-card">
                    <TableCell className="p-1.5">
                      <SearchableDescriptionSelect
                        value={line.description}
                        onValueChange={(v) => updateLine(idx, 'description', v)}
                        workspaceId={currentWorkspace?.workspace_id}
                        descriptions={descriptions}
                        onDescriptionCreated={(d) => {
                          if (!d.already_exists) setDescriptions(prev => [...prev, d]);
                        }}
                        placeholder="Item description"
                        className="h-9 text-sm border-0 bg-transparent focus-visible:ring-1"
                        testId={`line-desc-${idx}`}
                      />
                    </TableCell>
                    <TableCell className="p-1.5">
                      <SearchableAccountSelect
                        accounts={accounts} value={line.account_id}
                        onValueChange={(v) => updateLine(idx, 'account_id', v)}
                        workspaceId={currentWorkspace?.workspace_id}
                        onAccountCreated={(a) => setAccounts(prev => [...prev, a])}
                        accountType="income" placeholder="Account"
                        className="h-9" testId={`line-account-${idx}`}
                      />
                    </TableCell>
                    <TableCell className="p-1.5">
                      <Input type="number" value={line.quantity}
                        onChange={(e) => updateLine(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        className="h-9 text-center text-sm border-0 bg-transparent focus-visible:ring-1" />
                    </TableCell>
                    <TableCell className="p-1.5">
                      <CurrencyInput value={line.unit_price}
                        onValueChange={(v) => updateLine(idx, 'unit_price', v)}
                        className="h-9 text-right text-sm border-0 bg-transparent focus-visible:ring-1" />
                    </TableCell>
                    <TableCell className="p-1.5">
                      <Input type="number" value={line.tax_rate}
                        onChange={(e) => updateLine(idx, 'tax_rate', parseFloat(e.target.value) || 0)}
                        className="h-9 w-full text-center text-sm border-0 bg-transparent focus-visible:ring-1" />
                    </TableCell>
                    <TableCell className="text-right pr-3 text-sm font-medium text-muted-foreground whitespace-nowrap">
                      {formatCurrency(line.quantity * line.unit_price)}
                    </TableCell>
                    <TableCell className="p-1.5">
                      {form.line_items.length > 1 && (
                        <Button size="sm" variant="ghost" onClick={() => removeLine(idx)}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-rose-500">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Button variant="outline" size="sm" onClick={addLine} className="text-xs" data-testid="add-line-item-btn">
            <Plus className="w-3 h-3 mr-1.5" /> Add Line Item
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notes / Payment Instructions</Label>
            <Textarea value={form.memo}
              onChange={(e) => setForm({ ...form, memo: e.target.value })}
              placeholder="Payment terms, bank details, or any notes for the client..." rows={3}
              className="resize-none" />
          </div>

          <div className="flex justify-end">
            <div className="w-56 space-y-2 text-sm bg-muted rounded-xl p-4">
              <div className="flex justify-between text-muted-foreground">
                <span>Subtotal</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Tax</span>
                <span>{formatCurrency(tax)}</span>
              </div>
              <div className="flex justify-between font-semibold text-base border-t border-border pt-2 text-foreground">
                <span>Total</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Phase 2: Payments received + Refund (owner only). Only renders when
          there are actual payment records for this invoice. */}
      {isEdit && invoiceId && currentWorkspace?.workspace_id && (
        <Card>
          <CardContent className="p-4">
            <InvoicePayments
              invoiceId={invoiceId}
              workspaceId={currentWorkspace.workspace_id}
            />
          </CardContent>
        </Card>
      )}

      <div className="sticky bottom-0 -mx-4 sm:mx-0 bg-card/95 backdrop-blur border-t px-4 sm:px-0 py-3 flex justify-end gap-3 z-10">
        <Button variant="outline" onClick={() => navigate('/invoices')} data-testid="invoice-form-cancel-btn">
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white min-w-[160px]"
          data-testid="save-invoice-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (isEdit ? <Save className="w-4 h-4 mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />)}
          {isEdit ? 'Save Changes' : 'Create Invoice'}
        </Button>
      </div>
    </div>
  );
}
